import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import type { SelectOption } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound/types/select-option";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ColumnType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";

import { usePrevious } from "app/components/hooks/usePrevious";
import { translate } from "app/filters/i18nFilter";
import { WidgetBoxWithMarginBottom } from "app/pages/digital-marketing/ad-intelligence/common/StyledComponents";
import { useSidePanelWrapper } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/hooks/useSidePanelWrapper";
import { useAnalyzedUrls } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/hooks/useAnalyzedUrls";
import {
    EXCEL_API_ENDPOINT,
    INITIAL_TABLE_STATE,
    PAGE_SIZE,
    TABLE_ID,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/url-table/consts";
import { useSourcesUrlParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/url-table/hooks/useSourcesUrlParams";
import { SourcesReportTableSidePanel } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/url-table/SourcesReportTableSidePanel";
import { URLDataFilters } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/url-table/URLDataFilters";
import {
    getBaseWidgetRequestParams,
    getTopicsFilterParam,
    getWidgetNoDataProps,
    hasPreviousPeriodWindow,
    navigateToReport,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import { tableReducer } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/tableReducer";
import { useColumnPickerTracking } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/useColumnPickerTracking";
import type {
    ILLMReportWidget,
    LLModel,
    SortDirection,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { EAvailableTabs, ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { TableWrapper } from "app/pages/digital-marketing/generative-ai/shared/styledComponents";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getColumnConfig } from "./config";
import type { IAnalyzedURL, IURLFilterState } from "./types";

export const URLTableComponent = (reportParams: ILLMReportWidget) => {
    const {
        params: { topic: queryTopic, model },
    } = useCurrentStateAndParams();

    const {
        isOpen: isSidePanelOpen,
        selectedRow,
        openSidePanel,
        closeSidePanel,
    } = useSidePanelWrapper<IAnalyzedURL>({ analyticsLabel: "URL Analysis Side Panel" });

    const { campaign, campaignConfig, reportStatus, reportDuration } = reportParams;
    const containerRef = useRef<HTMLDivElement>(null);
    const previousCampId = usePrevious<string>(campaign?.id);
    const [activeRow, setActiveRow] = useState<IAnalyzedURL | undefined>();
    const [tableState, tableDispatch] = useReducer(
        tableReducer<IURLFilterState, keyof IAnalyzedURL>,
        INITIAL_TABLE_STATE,
    );

    const { currentPage, sorting } = tableState;
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const filterParams = useSourcesUrlParams();
    const globalTopics = useMemo(() => getTopicsFilterParam(queryTopic as string), [queryTopic]);
    const baseApiParams = useMemo(
        () => ({
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            ...(globalTopics ? { topics: globalTopics } : {}),
        }),
        [campaign, campaignConfig, reportDuration, globalTopics],
    );
    const hasPriorWindow = useMemo(() => hasPreviousPeriodWindow(reportDuration, campaign), [reportDuration, campaign]);

    const {
        data,
        isLoading: isLoadingData,
        isError,
    } = useAnalyzedUrls(
        {
            ...baseApiParams,
            pageSize: PAGE_SIZE,
            page: currentPage,
            sort: sorting.dataIndex,
            asc: sorting.sortDirection === "ascend",
            includeFacets: true,
            ...filterParams,
        },
        { enabled: canLoadData },
    );

    const tableData = useMemo<IAnalyzedURL[]>(() => data?.urls ?? [], [data]);
    const domainFacets = useMemo<SelectOption[]>(
        () =>
            data?.facets?.domains?.map(({ value, count }) => ({
                label: `${value} (${count})`,
                value,
                id: value,
            })) ?? [],
        [data?.facets?.domains],
    );
    const excelDownloadUrl = useMemo<string>(() => {
        const fetchService = DefaultFetchService.getInstance();
        const excelParams = {
            ...baseApiParams,
            sort: sorting.dataIndex,
            asc: sorting.sortDirection === "ascend",
            ...filterParams,
        };

        return `${EXCEL_API_ENDPOINT}?${fetchService.requestParams(excelParams)}`;
    }, [baseApiParams, sorting, filterParams]);

    const hasCompetitors = (campaign?.competitors?.length ?? 0) > 0;
    const config = useMemo<Array<ColumnType<IAnalyzedURL>>>(
        () => getColumnConfig(sorting, data?.totalCount ?? 0, hasCompetitors),
        [sorting, data?.totalCount, hasCompetitors],
    );

    const shouldShowNoData = isError || !data || reportStatus === ReportStatus.Error;
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;
    const noDataProps = getWidgetNoDataProps(shouldShowNoData, isError);

    useColumnPickerTracking("llmTrackerCitationsTableColumns", "Citation Analysis", containerRef);

    useEffect(() => {
        if (previousCampId && previousCampId !== campaign?.id) {
            tableDispatch({ type: "RESET_TABLE", payload: { sorting: INITIAL_TABLE_STATE.sorting } });
            navigateToReport(campaign, model as LLModel, EAvailableTabs.SOURCES);
        }
    }, [campaign, model, previousCampId, tableDispatch]);

    useEffect(() => {
        tableDispatch({ type: "RESET_PAGE" });
    }, [filterParams, baseApiParams, tableDispatch]);

    const onPageChange = (current: number, _pageSize: number, nextPage: number): void => {
        const paginationDirection = nextPage > current ? "next" : "prev";
        allTrackers.dispatchTrackEvent("Pagination", "click", `Table/${paginationDirection}/${nextPage}`);
        tableDispatch({ type: "SET_PAGE", payload: nextPage });
    };

    const handleSortBy = (dataIndex: keyof IAnalyzedURL, sortDirection: SortDirection): void => {
        if (sorting.dataIndex === dataIndex && sorting.sortDirection === sortDirection) {
            return;
        }
        tableDispatch({ type: "SET_SORTING_AND_RESET_PAGE", payload: { dataIndex, sortDirection } });
    };

    const handleSearchChange = (term: string): void => {
        void proNavigator.updateParams({ searchTerm: term || undefined }, { reload: false });
    };

    const trackExcelDownload = (): void => allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel");

    return (
        <>
            <WidgetBoxWithMarginBottom
                ref={containerRef}
                data-automation="url-analysis-widget"
            >
                <TableWrapper>
                    <Table
                        key={`${campaign.id}-${hasCompetitors}`}
                        id={TABLE_ID}
                        size="large"
                        localStorageKey="llmTrackerCitationsTableColumns"
                        pagination={{
                            pageSize: PAGE_SIZE,
                            total: data?.totalCount ?? 0,
                            current: currentPage,
                            onChange: onPageChange,
                        }}
                        onSort={(sorterData) => {
                            if (!sorterData.order) {
                                return;
                            }
                            handleSortBy(sorterData.columnKey as keyof IAnalyzedURL, sorterData.order);
                        }}
                        activeRowSelector={(row) => row.url === activeRow?.url}
                        onRow={(record) => ({
                            onClick: () => {
                                setActiveRow(record);
                                openSidePanel(record);
                            },
                            style: { cursor: "pointer" },
                        })}
                        rowHoverable
                        sticky={false}
                        columnConfig={config}
                        data={tableData}
                        theme="gray"
                        loaderRowCount={PAGE_SIZE}
                        isLoading={shouldShowLoading}
                        noData={{
                            title: noDataProps.noDataTitle,
                            subtitle: noDataProps.noDataSubtitle,
                        }}
                    >
                        <Table.TableTop borderBottom>
                            <Table.TableTop.Title
                                title={translate("llm.url_analysis.title")}
                                tooltip={translate("llm.url_analysis.tooltip")}
                            />
                            <Table.TableTop.Settings
                                locale={{
                                    columnPicker: {
                                        mandatoryField: translate("common.columnsPicker.mandatoryField"),
                                    },
                                }}
                                columnPickerType="reorderable"
                                excelExportConfig={{
                                    excelDownloadUrl,
                                    onExcelDownloaded: trackExcelDownload,
                                }}
                            />
                            <Table.TableTop.Filters
                                filters={[
                                    {
                                        filter: "search",
                                        placeholder: translate("llm.url_analysis.search.placeholder"),
                                        onSearchChange: handleSearchChange,
                                        initialValue: filterParams.searchTerm,
                                    },
                                ]}
                                factoryFilters={
                                    <URLDataFilters
                                        domainFacets={domainFacets}
                                        isLoading={shouldShowLoading}
                                        hasPriorWindow={hasPriorWindow}
                                    />
                                }
                            />
                        </Table.TableTop>
                    </Table>
                </TableWrapper>
            </WidgetBoxWithMarginBottom>
            <SourcesReportTableSidePanel
                campaign={campaign}
                campaignConfig={campaignConfig}
                reportDuration={reportDuration}
                isOpen={isSidePanelOpen}
                urlData={selectedRow as IAnalyzedURL}
                topics={globalTopics}
                onClose={() => {
                    closeSidePanel();
                    setActiveRow(undefined);
                }}
            />
        </>
    );
};
